import Vue from 'vue';
import {
  DefaultSpeedSelection,
  DefaultVoiceSelection,
  Gender,
  Language,
  ReadingMode,
  SpeedSelection,
  VoiceSelection
} from './models';
import { AppConstants } from '@/types/appConstants';

export interface ReadAloudState {
  subscribers: ((range: Range) => void)[];
  currentVoiceSelection: VoiceSelection;
  currentSpeedSelection: SpeedSelection;
  currentRange: Range | null;
  startSectionId: number;
  isPlayerCurrentlyActive: boolean;
  isPlayerShowing: boolean;
  readingMode: ReadingMode;
  settingsChanged: boolean;
  isPaused: boolean;
}

export const state = Vue.observable<ReadAloudState>({
  subscribers: [],
  currentVoiceSelection: DefaultVoiceSelection,
  currentSpeedSelection: DefaultSpeedSelection,
  startSectionId: 0,
  isPlayerCurrentlyActive: false,
  isPlayerShowing: false,
  readingMode: ReadingMode.Selection,
  currentRange: null,
  settingsChanged: false,
  isPaused: false
});

class ReadAloudStore {
  public get genders(): Array<Gender> {
    return [Gender.Female, Gender.Male];
  }

  public get languages(): Array<Language> {
    return [
      Language.Danish,
      Language.Spanish,
      Language.German,
      Language.English,
      Language.French,
      Language.Icelandic,
      Language.Swedish
    ];
  }

  public get voiceSelections(): VoiceSelection[] {
    return [
      { voice: 1, gender: Gender.Female, language: Language.Danish },
      { voice: 2, gender: Gender.Male, language: Language.Danish },
      { voice: 3, gender: Gender.Female, language: Language.English },
      { voice: 4, gender: Gender.Male, language: Language.English },
      { voice: 5, gender: Gender.Female, language: Language.French },
      { voice: 6, gender: Gender.Male, language: Language.French },
      { voice: 7, gender: Gender.Female, language: Language.German },
      { voice: 8, gender: Gender.Male, language: Language.German },
      { voice: 9, gender: Gender.Female, language: Language.Spanish },
      { voice: 10, gender: Gender.Male, language: Language.Spanish },
      { voice: 11, gender: Gender.Male, language: Language.Icelandic },
      { voice: 12, gender: Gender.Female, language: Language.Icelandic },
      { voice: 13, gender: Gender.Female, language: Language.Swedish }
    ];
  }

  public get defaultSettingsForReadAloud(): ReadAloudState {
    return {
      currentSpeedSelection: DefaultSpeedSelection,
      currentVoiceSelection: DefaultVoiceSelection,
      startSectionId: 0,
      isPlayerCurrentlyActive: state.isPlayerCurrentlyActive,
      isPlayerShowing: state.isPlayerShowing,
      subscribers: state.subscribers,
      readingMode: ReadingMode.Selection,
      currentRange: null,
      settingsChanged: false,
      isPaused: false
    };
  }

  public get isStateDifferentFromDefault(): boolean {
    return (
      state.currentSpeedSelection.speed !== DefaultSpeedSelection.speed ||
      state.currentVoiceSelection.voice !== DefaultVoiceSelection.voice
    );
  }

  public get currentRange(): Range | null {
    return state.currentRange;
  }

  public get startSectionId(): number {
    return state.startSectionId;
  }

  public set startSectionId(startSectionId: number) {
    state.startSectionId = startSectionId;
  }

  public get voice(): VoiceSelection {
    return state.currentVoiceSelection;
  }

  public set voice(voice: VoiceSelection) {
    state.currentVoiceSelection = voice;
  }

  public get speed(): SpeedSelection {
    return state.currentSpeedSelection;
  }

  public set speed(speed: SpeedSelection) {
    state.currentSpeedSelection = speed;
  }

  public get playerCurrentlyActive(): boolean {
    return state.isPlayerCurrentlyActive;
  }

  public set playerCurrentlyActive(playing: boolean) {
    state.isPlayerCurrentlyActive = playing;
  }

  public get playerShowing(): boolean {
    return state.isPlayerShowing;
  }

  public set playerShowing(showing: boolean) {
    state.isPlayerShowing = showing;
  }

  public get readingMode(): ReadingMode {
    return state.readingMode;
  }

  public set readingMode(mode: ReadingMode) {
    state.readingMode = mode;
  }

  public get isPaused(): boolean {
    return state.isPaused;
  }

  public set isPaused(bool: boolean) {
    state.isPaused = bool;
  }

  // Only checks for voice, lang and gender changes
  public get defaultVoiceSettingsChanged(): boolean {
    return (
      DefaultVoiceSelection.voice !== state.currentVoiceSelection.voice &&
      DefaultVoiceSelection.language !== state.currentVoiceSelection.language &&
      DefaultVoiceSelection.gender !== state.currentVoiceSelection.gender
    );
  }

  public initReadPage(): void {
    state.readingMode = ReadingMode.Document;
  }

  public subscribeReadAloud(caller: (range: Range) => void) {
    state.subscribers.push(caller);
  }

  public clearSubscribers() {
    state.subscribers = [];
  }

  public notifyReadAloud(range: Range) {
    state.currentRange = range;
    state.subscribers.forEach((caller) => caller(range));
  }

  public resetReadAloudSettings(): void {
    state.currentSpeedSelection = DefaultSpeedSelection;
    state.currentVoiceSelection = DefaultVoiceSelection;
  }

  public get settingsChanged(): boolean {
    return state.settingsChanged;
  }

  public set settingsChanged(changed: boolean) {
    state.settingsChanged = changed;
  }

  private static fetchState(): ReadAloudState | null {
    const item = localStorage.getItem(AppConstants.LOCAL_STORAGE_READ_ALOUD);
    if (item) {
      return JSON.parse(item);
    }

    return null;
  }
}

export default new ReadAloudStore();
